import { getUserMenuItemsLookup } from './user';
import { getBrowseEventsItems } from './getBrowseEventsItems';
import { DIVIDER } from '@eventbrite/legacy-eds.nav-list-item/constants';

/**
 * Get info for the user menu typically passed to the `userMenuInfo` prop of EDS Global Header
 * @param {object} options
 * @param {string} options.serverUrl Base site URL (e.g. "http://www.eventbrite.com")
 * @param {string} options.adminServerUrl Base Admin site URL (e.g. "http://admin.eventbrite.com")
 * @param {string} options.logoutUrl URL to logout page
 * @param {string} [options.requestPath] Current request path (for analytics)
 * @param {object} options.user User object
 * @param {boolean} [options.user.hasTikibar] Whether user has access to Tikibar
 * @param {boolean} [options.user.isAttendee] Whether user is an attendee
 * @param {boolean} [options.user.isAuthenticated] Whether user is authenticated
 * @param {boolean} [options.user.isStaff] Whether user is staff
 * @param {string} [options.user.publicId] User's public ID
 * @param {string} [options.user.thumbnailUrl] User's thumbnail URL
 * @param {string} [options.user.fullName] User's full name
 * @param {string} [options.user.email] User's email
 * @param {boolean} [options.showBrowseEvents] Whether or not to include Browse Events menu item
 * @param {boolean} [options.isManageOverviewActive] Whether or not the new Manage Overview page is active
 * @param {string} [options.iconColor] Color of the user icon
 * @param {Record<string, boolean>} [options.featureFlags] Feature flags
 * @param {boolean} [options.isMobile] Whether on mobile
 * @param {object} [options.helpUrls] Help URLs
 * @param {string} [options.helpUrls.home] Home URL
 * @param {boolean} [options.showHelpMenu] Whether or not to show the Help menu item
 */
export const getUserMenuInfo = ({
    serverUrl,
    adminServerUrl,
    logoutUrl,
    requestPath,
    user: {
        hasTikibar,
        isAttendee,
        isAuthenticated,
        isStaff,
        publicId,
        thumbnailUrl,
        fullName,
        email,
    },
    showBrowseEvents,
    isManageOverviewActive,
    iconColor = undefined,
    featureFlags = {},
    isMobile = false,
    helpUrls,
    showHelpMenu = false,
}) => {
    if (!isAuthenticated) {
        return null;
    }

    const {
        launchCreatorCreditsSectionAttendee: canUseCredits,
        enableOldCollectionsLinkInHeader: canUseCollections,
        isDetachInterestsEnabled: isDetachedInterests,
    } = featureFlags;

    const accountSettingsPath = 'account-settings';

    return {
        iconColor,
        label: email,
        fallbackUrl: `${serverUrl}/${accountSettingsPath}/`,
        imageUrl: thumbnailUrl,
        navItems: _getUserMenuItems({
            adminServerUrl,
            canUseCredits,
            canUseCollections,
            hasTikibar,
            isAttendee,
            isStaff,
            logoutUrl,
            publicId,
            requestPath,
            serverUrl,
            accountSettingsPath,
            showBrowseEvents,
            fullName,
            email,
            userImageUrl: thumbnailUrl,
            isManageOverviewActive,
            isMobile,
            isDetachedInterests,
            helpUrls,
            showHelpMenu,
        }),
    };
};

/**
 * Retrieves the list of attendee-related user menu items given the specified configurations
 * @param {object} options
 * @param {object} options.userMenuItemsLookup Lookup of all user menu items
 * @param {boolean} options.canUseCredits Whether or not the user has access to credits
 * @param {boolean} options.canUseCollections Whether or not the user has access to collections
 * @param {boolean} options.showBrowseEvents Whether or not to show the "Browse events" menu item
 * @returns {object[]}
 */
export const getAttendeeUserMenuItems = ({ userMenuItemsLookup, canUseCollections }) => {
    const { tickets, saved, following, collections, interests } = userMenuItemsLookup;
    const attendeeItems = [];

    attendeeItems.push(tickets);

    attendeeItems.push(saved);
    attendeeItems.push(following);

    if (canUseCollections) {
        attendeeItems.push(collections);
    }

    attendeeItems.push(interests);

    return attendeeItems;
};

/**
 * Retrieves the list of staff-related user menu items given the specified configurations
 * @param {object} options
 * @param {object} options.userMenuItemsLookup Lookup of all user menu items
 * @param {boolean} options.isStaff Whether or not the account is a staff account
 * @param {boolean} options.hasTikibar Whether or not the account has access to Tikibar
 * @returns {object[]}
 */
export const getStaffUserMenuItems = ({ userMenuItemsLookup, isStaff, hasTikibar }) => {
    if (!isStaff) {
        return [];
    }

    const { admin, tikibar, featureFlags } = userMenuItemsLookup;
    const menuItems = [admin];

    if (hasTikibar) {
        menuItems.push(tikibar);
    }

    menuItems.push(featureFlags, { type: DIVIDER });

    return menuItems;
};

const _getUserMenuItems = ({
    isMobile,
    adminServerUrl,
    canUseCredits,
    canUseCollections,
    hasTikibar,
    isAttendee,
    isStaff,
    logoutUrl,
    publicId,
    requestPath,
    serverUrl,
    accountSettingsPath,
    showBrowseEvents,
    email,
    fullName,
    userImageUrl,
    isManageOverviewActive,
    isDetachedInterests,
    helpUrls,
    showHelpMenu,
}) => {
    const userMenuItemsLookup = getUserMenuItemsLookup({
        adminServerUrl,
        logoutUrl,
        publicId,
        requestPath,
        serverUrl,
        accountSettingsPath,
        email,
        fullName,
        userImageUrl,
        isManageOverviewActive,
        isDetachedInterests,
        helpUrls,
    });
    const staffUserMenuItems = getStaffUserMenuItems({
        userMenuItemsLookup,
        hasTikibar,
        isStaff,
    });
    const browseEventsItems = getBrowseEventsItems({
        userMenuItemsLookup,
        showBrowseEvents,
    });
    const manageEvents = _getManageMyEventsItems({
        userMenuItemsLookup,
        isAttendee,
        isMobile,
    });
    const attendeeMenuItems = getAttendeeUserMenuItems({
        userMenuItemsLookup,
        canUseCredits,
        canUseCollections,
        showBrowseEvents,
    });

    const helpMenuItems = getHelpItems({
        userMenuItemsLookup,
        showHelpMenu,
    });

    return [
        ...staffUserMenuItems,
        ...browseEventsItems,
        ...manageEvents,
        ...helpMenuItems,
        { type: DIVIDER },
        ...attendeeMenuItems,
        { type: DIVIDER },
        userMenuItemsLookup.accountSettings,
        userMenuItemsLookup.logout,
    ];
};

const _getManageMyEventsItems = ({ isAttendee, userMenuItemsLookup, isMobile }) => {
    if (isAttendee && !isMobile) {
        return [];
    }

    const { manageEvents } = userMenuItemsLookup;

    return [manageEvents];
};

/**
 * Retrieves the help menu items given the specified configurations
 * @param {object} options
 * @param {object} options.userMenuItemsLookup Lookup of all user menu items
 * @param {boolean} options.showHelpMenu Whether or not to show the Help menu item
 * @returns {object[]}
 */
const getHelpItems = ({ userMenuItemsLookup, showHelpMenu }) => {
    if (!showHelpMenu) {
        return [];
    }
    const { help } = userMenuItemsLookup;
    return [help];
};
